import { formatBigNumber } from "@quint/services/web3";
import { useQuintBalance } from "@quint/services/web3/hooks";
import { Button, Input, type ButtonProps } from "@quint/ui";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { type MouseEvent, useCallback } from "react";
import { useAccount } from "wagmi";
import { UnstakeDialog } from "./unstake-dialog";

interface MaxProps {
  setValue: (v: number) => void;
  disabled?: boolean;
}

const MaxButton = (props: MaxProps) => {
  const { setValue, disabled } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const balanceQuery = useQuintBalance();

  const maxBalance = Math.max(+formatBigNumber(balanceQuery.data?.value) - 1, 0);
  const onClick = useCallback(() => {
    console.log("isConnected", isConnected, openConnectModal);
    if (!isConnected) {
      openConnectModal?.();
      return;
    }
    if (balanceQuery.isLoading) {
      return;
    }
    setValue(maxBalance);
  }, [balanceQuery.isLoading, isConnected, maxBalance, openConnectModal, setValue]);

  return (
    <Button variant="text" className="h-auto rounded-md py-0 px-2" onClick={onClick} disabled={disabled}>
      max
    </Button>
  );
};
export const TokensInput = ({
  value,
  setValue,
  disabled,
  allowMax = true,
}: {
  value: number | undefined;
  setValue: (v: number | undefined) => void;
  disabled?: boolean;
  allowMax?: boolean;
}) => {
  return (
    <Input
      min={0}
      number
      value={value}
      disabled={disabled}
      onChange={(e) => {
        const { value } = e.target;

        if (value && Number(value) >= 0) setValue(Number(value));
        else if (Number(value) < 0) setValue(0);
        else setValue(undefined);
      }}
      endAdornment={allowMax ? <MaxButton setValue={setValue} disabled={disabled} /> : null}
    />
  );
};

interface HarvestButtonProps {
  deprecated: boolean;
}
const HarvestButton = (props: HarvestButtonProps & ButtonProps) => {
  const { deprecated, onClick, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  return (
    <Button
      as="div"
      className="w-full"
      variant={deprecated ? "primary" : "primaryGradient"}
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
        if (!isConnected) {
          openConnectModal?.();
        } else {
          onClick?.(e);
        }
      }}
      {...rest}
    >
      Harvest
    </Button>
  );
};

const UnstakeButton = (props: ButtonProps) => {
  const { onClick, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        onClick?.(e);
      }
    },
    [isConnected, onClick, openConnectModal]
  );

  return (
    <Button className="w-full" variant="primary" onClick={handleClick} {...rest}>
      Unstake
    </Button>
  );
};

const MigrateButton = (props: ButtonProps) => {
  const { onClick, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        onClick?.(e);
      }
    },
    [isConnected, onClick, openConnectModal]
  );

  return (
    <Button className="w-full" variant="primaryGradient" onClick={handleClick} {...rest}>
      Migrate Rewards
    </Button>
  );
};

const StakeButton = (props: ButtonProps) => {
  const { onClick, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        onClick?.(e);
      }
    },
    [isConnected, onClick, openConnectModal]
  );

  return (
    <Button className="w-full" variant="primaryGradient" onClick={handleClick} {...rest}>
      Stake
    </Button>
  );
};

type UnstakeDialogSubmitButtonProps = {
  fullUnstake: boolean;
} & ButtonProps;
const UnstakeDialogSubmitButton = (props: UnstakeDialogSubmitButtonProps) => {
  const { onClick, fullUnstake, ...rest } = props;
  const { isConnected } = useAccount();
  const { openConnectModal } = useConnectModal();
  const handleClick = useCallback(
    (e: MouseEvent<HTMLButtonElement, globalThis.MouseEvent>) => {
      if (!isConnected) {
        openConnectModal?.();
      } else {
        onClick?.(e);
      }
    },
    [isConnected, onClick, openConnectModal]
  );
  return (
    <Button className="w-full md:w-[50%]" variant="secondary" onClick={handleClick} {...rest}>
      {fullUnstake ? "Full Unstake" : "Unstake"}
    </Button>
  );
};
const Staking = {
  HarvestButton,
  TokensInput,
  UnstakeButton,
  MigrateButton,
  StakeButton,
  UnstakeDialog,
  UnstakeDialogSubmitButton,
};
export default Staking;
